// The data adapter is the only place that knows where clinic data lives.
// Today it is the browser's localStorage. To connect a backend later, write
// another adapter with the same three async methods (for example calling a
// REST API) and pass it to ClinicDataProvider. Nothing else needs to change.

import type { ClinicData, ClinicSettings } from '@/types/clinic'

import { DATA_VERSION, DEFAULT_SETTINGS, STORAGE_KEY } from '@/constants/clinic'

export interface ClinicDataAdapter {
    load: () => Promise<ClinicData | null>
    save: (data: ClinicData) => Promise<void>
    clear: () => Promise<void>
}

const isObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const toNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0)

const records = <T>(value: unknown, fill: (item: Record<string, unknown>) => T): T[] => {
    if (!Array.isArray(value)) return []
    return value.filter(item => isObject(item) && typeof item.id === 'string').map(item => fill(item as Record<string, unknown>))
}

const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])

const highestSequence = (values: string[]): number => {
    return values.reduce((highest, value) => {
        const match = /(\d+)$/.exec(value)
        return match ? Math.max(highest, Number(match[1])) : highest
    }, 0)
}

export const mergeSettings = (raw: unknown): ClinicSettings => {
    const source = isObject(raw) ? raw : {}
    const section = <K extends keyof ClinicSettings>(key: K): ClinicSettings[K] => ({
        ...DEFAULT_SETTINGS[key],
        ...(isObject(source[key]) ? (source[key] as Partial<ClinicSettings[K]>) : {})
    })
    const whatsapp = section('whatsapp')

    return {
        clinic: section('clinic'),
        appointments: section('appointments'),
        prescription: section('prescription'),
        billing: section('billing'),
        whatsapp: {
            ...whatsapp,
            templates: {
                ...DEFAULT_SETTINGS.whatsapp.templates,
                ...(isObject(whatsapp.templates) ? whatsapp.templates : {})
            }
        }
    }
}

// Accepts anything (a stored value or an imported file) and returns clean,
// complete clinic data, or null when it is not clinic data at all.
export const normalizeClinicData = (value: unknown): ClinicData | null => {
    if (!isObject(value)) return null

    const required = ['patients', 'treatments', 'visits', 'appointments', 'prescriptions', 'invoices', 'followUps', 'messages']
    if (!required.every(key => Array.isArray(value[key]))) return null

    const patients = records(value.patients, item => item as unknown as ClinicData['patients'][number])
    const treatments = records(value.treatments, item => item as unknown as ClinicData['treatments'][number])
    const visits = records(value.visits, item => ({ ...item, treatments: list(item.treatments) }) as unknown as ClinicData['visits'][number])
    const appointments = records(value.appointments, item => item as unknown as ClinicData['appointments'][number])
    const prescriptions = records(value.prescriptions, item => ({ ...item, medicines: list(item.medicines) }) as unknown as ClinicData['prescriptions'][number])
    const invoices = records(value.invoices, item => ({ ...item, items: list(item.items), payments: list(item.payments) }) as unknown as ClinicData['invoices'][number])
    const followUps = records(value.followUps, item => ({ ...item, history: list(item.history) }) as unknown as ClinicData['followUps'][number])
    const messages = records(value.messages, item => item as unknown as ClinicData['messages'][number])
    const counters = isObject(value.counters) ? value.counters : {}

    return {
        version: DATA_VERSION,
        seededOn: typeof value.seededOn === 'string' ? value.seededOn : '',
        patients,
        treatments,
        visits,
        appointments,
        prescriptions,
        invoices,
        followUps,
        messages,
        settings: mergeSettings(value.settings),
        counters: {
            patient: Math.max(toNumber(counters.patient), highestSequence(patients.map(item => String(item.code ?? ''))), 1000),
            prescription: Math.max(toNumber(counters.prescription), highestSequence(prescriptions.map(item => String(item.number ?? '')))),
            invoice: Math.max(toNumber(counters.invoice), highestSequence(invoices.map(item => String(item.number ?? ''))))
        }
    }
}

export const localStorageAdapter: ClinicDataAdapter = {
    load: async () => {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        try {
            return normalizeClinicData(JSON.parse(raw))
        }
        catch {
            return null
        }
    },
    save: async (data) => {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    },
    clear: async () => {
        window.localStorage.removeItem(STORAGE_KEY)
    }
}
